import { AbstractBlockFactory } from '../block/AbstractBlockFactory';
import {
    ComponentSettingsChartMeasureField,
    ComponentSettingsLineChartAxis,
    LineChartSeriesType,
    NewBlockContextType,
    BlockType,
    DatasetField,
    fieldToChartMeasuredField,
    LineChartInitialDataResponse,
    MeasureFieldAggregationType,
    DatasetFieldValueType,
    MeasureFieldDisplayType,
    MeasureField,
} from '@report-content';
import { YNValues } from 'tools/types';
import { ChartComponentSettings, AllowedMeasureFieldAggregations } from './line-chart';
import { TFunction } from 'i18next';

export const KpiAllowedMeasureFieldAggregations: Array<MeasureFieldAggregationType> = [
    { label: 'None', name: 'None', value_type: 'numeric' as DatasetFieldValueType },
].concat(AllowedMeasureFieldAggregations);

export const KpiAllowedMeasureFieldDisplayTypes: Array<MeasureFieldDisplayType> = [
    { name: 'line', label: 'Line', icon: { type: 'mi', value: 'line-chart-with-arrow' } },
    { name: 'column', label: 'Bar', icon: { type: 'mi', value: 'bar-chart' } },
];

export class BlockFactoryKpiChart extends AbstractBlockFactory<ComponentSettingsKpiChart> {
    public getNewBlock({
        nextUId,
        nextSortOrder,
        datasetFields,
    }: NewBlockContextType): BlockType<ComponentSettingsKpiChart> {
        const uid = 'block_uid_' + nextUId;
        return {
            // TODO:
            component: {
                datasetRendererBlockComponentId: nextUId,
                datasetRendererBlockId: nextUId,
                datasetRendererComponentId: nextUId,
                displayOrder: nextSortOrder,
                internalName: 'kpi_chart',
                settings: {
                    uid: uid,
                    title: '',
                    height: 600,
                    show_in_viewer: 'Y',
                    show_summary_in_viewer: 'Y',
                    is_preview_ind: 'N',
                    include_in_email: 'Y',
                    is_collapsed_ind: 'N',
                    include_in_email_attachment: 'Y',
                    internal_name: 'kpi_chart',
                    groupFilter: { operator: 'AND', rules: [] },
                    queryAgainstEntireDataset: 'N',
                    dataQueries: {
                        table: {
                            resultFieldAggregations: {},
                            resultFields: [],
                            sort: [],
                        },
                    },
                    measureFields: [],
                    series: 'single',
                    split_by_filter_values: 'N',
                    x_axis: {
                        field: '',
                        title: '',
                        value_type: 'numeric',
                        defaultZoom: '',
                        display_mask_id: '0',
                    },
                    y_axis: [{ field: '', title: '', value_type: 'numeric', defaultZoom: '', display_mask_id: '0' }],
                    isOnCreation: true,
                    stoplight: {
                        includeStoplight: 'N',
                        stoplightMoreIsBetter: 'Y',
                        stoplightCalcMethod: 'fixed range',
                        stoplightColorForHighValues: '#077E45',
                        stoplightColorForLowValues: '#AC2B2B',
                        stoplightOkRangeStart: 0,
                        stoplightOkRangeStartComparisonType: 'strict',
                        stoplightOkRangeEnd: 0,
                        stoplightOkRangeEndComparisonType: 'strict',
                        stoplightCompareValueCalcMethodId: 0,
                        stoplightCompareValuePeriod: 0,
                        stoplightCompareValueUnit: 'months',
                        stoplightTarget: null,
                    },
                    kpiVariance: {
                        includeKpiVariance: 'N',
                        varianceCalcMethod: 'prior values',
                        varianceCompareValuePeriod: 0,
                        varianceCompareValueUnit: 'months',
                        varianceTarget: null,
                        showVarianceAs: 'percentage',
                    },
                    alertRules: [],
                },
            },
            // datasetRendererBlockId: number;
            displayOrder: nextSortOrder,
            // elementId: number;
            uid: uid,
        };
    }
}

export type StoplightComparisonType = 'strict' | 'non strict';
export type CalcMethodType = 'fixed range' | 'prior values' | 'target comparison';
export interface IKpiChartStoplightSettings {
    includeStoplight: YNValues;
    stoplightMoreIsBetter: YNValues;
    stoplightCalcMethod: CalcMethodType;
    stoplightColorForHighValues: string;
    stoplightColorForLowValues: string;
    stoplightOkRangeStart: number;
    stoplightOkRangeStartComparisonType: StoplightComparisonType;
    stoplightOkRangeEnd: number;
    stoplightOkRangeEndComparisonType: StoplightComparisonType;
    stoplightCompareValueCalcMethodId: number;
    stoplightCompareValuePeriod: number;
    stoplightCompareValueUnit: string;
    stoplightTarget: MeasureField | null;
}

export type VarianceCalcMethodType = 'prior values' | 'target comparison';

export interface IKpiChartVarianceSettings {
    includeKpiVariance: YNValues;
    varianceCalcMethod: VarianceCalcMethodType;
    varianceCompareValuePeriod: number;
    varianceCompareValueUnit: string;
    varianceTarget: MeasureField | null;
    showVarianceAs: 'percentage' | 'absolute';
}

export interface IKpiAlertRule {
    report_alert_rule_id: number;
    name: string;
}

export type KpiChartOverlayAxis = 'Left' | 'Right';
export type KpiChartOverlayType = 'Line' | 'Bar';

export interface IKpiChartOverlay {
    id: string;
    title: string;
    chart_type: KpiChartOverlayType;
    dash_style: string;
    axis: KpiChartOverlayAxis;
    color: string;
}

export interface ComponentSettingsKpiChart extends ChartComponentSettings {
    uid: string;
    show_summary_in_viewer: YNValues;
    measureFields: Array<ComponentSettingsKpiChartMeasureField>;
    queryAgainstEntireDataset: YNValues;
    series: LineChartSeriesType;
    // Optional: absent on blocks saved before the option existed
    split_by_filter_values?: YNValues;
    x_axis: ComponentSettingsLineChartAxis;
    y_axis?: Array<ComponentSettingsLineChartAxis>;
    stoplight: IKpiChartStoplightSettings;
    kpiVariance: IKpiChartVarianceSettings;
    alertRules: Array<IKpiAlertRule>;
}

export interface ComponentSettingsKpiChartMeasureField extends ComponentSettingsChartMeasureField {
    is_primary: YNValues;
    is_split_by_filter?: YNValues;
    overlay_in_tile: YNValues;
    overlay_in_chart: YNValues;
    show_data_labels: YNValues;
    custom_label: string;
}

export function fieldToKpiChartMeasuredField(field: DatasetField): ComponentSettingsKpiChartMeasureField {
    const chartMeasure = fieldToChartMeasuredField(field);
    return {
        ...chartMeasure,
        is_primary: 'N',
        is_split_by_filter: 'N',
        overlay_in_tile: 'N',
        overlay_in_chart: 'N',
        show_data_labels: 'N',
        custom_label: '',
    } as ComponentSettingsKpiChartMeasureField;
}

export function getKpiChartMeasureFieldDisplayLabel(field: ComponentSettingsKpiChartMeasureField): string {
    return field.custom_label ? field.custom_label : field.column_name;
}

export function getKpiChartVariancePeriodLabel(
    varianceSettings: Pick<IKpiChartVarianceSettings, 'varianceCompareValuePeriod' | 'varianceCompareValueUnit'>,
    t: TFunction,
): string {
    const unitKey = varianceSettings.varianceCompareValueUnit.replace(/s$/, '') + '_label';
    return `${varianceSettings.varianceCompareValuePeriod} ${t(unitKey, { count: Number(varianceSettings.varianceCompareValuePeriod) })}`;
}

export interface IKpiResults {
    [key: string]: number | string | null;
    stoplight_bad_threshold: number | null;
    stoplight_good_threshold: number | null;
    moving_avg: number | null;
    standard_deviation: number | null;
}

export interface IKpiChartOverlayDataRow {
    date: string;
    value: number | string | null;
    'value-formatted': string | null;
}

// Axis display masks default to '0' (Default - no override, format falls back to the axis' own logic).
export interface IKpiChartOverlaysSettings {
    overlays: Array<IKpiChartOverlay>;
    left_axis_display_mask_id?: string;
    right_axis_display_mask_id?: string;
}

// Keyed by KPI Chart block uid - one report can have several KPI Chart blocks, each with its own overlays.
export interface IReportOverlaysState {
    [blockUid: string]: IKpiChartOverlaysSettings;
}

export interface IReportState {
    id: number;
    filters: { [key: string]: any };
    state: { overlays?: IReportOverlaysState };
}

export interface IKpiVarianceResult {
    variance: number;
    baseline_value: number;
    target_value: number | null;
    comparison_basis: string;
    variance_mode: 'percentage' | 'absolute';
    variance_formatted?: string;
    baseline_value_formatted?: string;
    target_value_formatted?: string;
}

export interface KpiChartInitialDataResponse extends LineChartInitialDataResponse {
    kpiResults: IKpiResults[];
    alertEvents: IKpiAlertEvent[];
    kpiVarianceResult?: IKpiVarianceResult;
}

// If Snapshot Date is used as a measurement time, the result_data will be empty.
// Use measurement_time, title and filters as Alert Event data.
// If other date column is used as measurement time, the result_data will be used to get the Alert Event data,
// and measurement_time, title and filters will be ignored.
export interface IKpiAlertEvent {
    measurement_time: string;
    title: string;
    filters: IKpiAlertEventFilters;
    result_data: IKpiAlertEventResultData[];
}

export interface IKpiAlertEventResultData {
    title: string;
    row: IKpiAlertEventFilters;
}

export interface IKpiAlertEventFilters {
    [key: string]: number | string;
}
